import { useFeatureFlag } from "@src/backpack-console/featureFlags";
import { LeftNav, LeftNavItem } from "@src/straps/base/navigation/LeftNav";
import { ClickableDiv } from "@src/straps/utils/ClickableDiv";
import { PropsWithChildren, ReactElement } from "react";
import { useRouteMatch } from "react-router-dom";
import { LeftNavGroup } from "../../../straps/base/navigation/LeftNav/LeftNavGroup";
import { RebatesItem } from "./RebatesItem";
import { SelectedBuildingSection } from "./SelectedBuildingSection";

function ExternalLinkItem({
  link,
  children,
}: PropsWithChildren<{
  readonly link: string;
  readonly children: ReactElement;
}>) {
  return (
    <ClickableDiv
      onClick={() => window.open(link, "_blank")}
      data-testid="backpack-left-nav-external-link"
    >
      {children}
    </ClickableDiv>
  );
}

function PortfolioNavGroup({ children }: React.PropsWithChildren) {
  const siteMatch = useRouteMatch("/site/:siteId");
  return (
    <LeftNavGroup
      title="Portfolio"
      iconName="portfolio"
      isExpandable={Boolean(siteMatch)}
    >
      {children}
    </LeftNavGroup>
  );
}

export default function BackpackLeftNav() {
  const isDemoEnabled = useFeatureFlag("demo-features", "off") === "on";
  const dataPackageLink =
    "https://airtable.com/appjaegpBE9VqhEK0/pag8gjzKiwVJ9nrPt";
  const insuranceSOVLink =
    "https://airtable.com/app1vUj2eICyAXu0F/shrTKglMBs6wMr8oW/tbl1RhhRXEnqyjSXL";

  const dashboardItems = (
    <>
      <LeftNavItem
        title="Onboarding Completion"
        path="/onboarding-completion"
        iconName="onboarding-guide-completed"
        persistQueries={["accountId"]}
      />
      <LeftNavItem
        title="Environmental Impact"
        path="/environmental-impact"
        iconName="environmental-impact"
        persistQueries={["accountId"]}
      />
      <LeftNavItem
        title="Certifications"
        path="/certifications"
        iconName="certifications"
        exact
      />
      <LeftNavItem title="Projects" path="/projects" iconName="project" />
      <LeftNavItem title="Equipment" path="/equipment" iconName="equipment" />
    </>
  );

  const portfolioItems = (
    <>
      <LeftNavItem
        title="Backpack Homepage"
        path="/"
        iconName="backpack"
        persistQueries={["accountId"]}
      />
      <LeftNavItem
        title="Marketplace"
        path="/marketplace"
        iconName="marketplace"
      />
      <LeftNavItem title="Dashboards" iconName="dashboard">
        {dashboardItems.props.children}
      </LeftNavItem>
      <LeftNavItem title="Reports" path="/reports" iconName="reports">
        <LeftNavItem
          title="Property Overview"
          path="/reports/property-overview"
          persistQueries={["accountId"]}
        />
        <LeftNavItem
          title="Energy Star Status"
          path="/reports/energy-star"
          persistQueries={["accountId"]}
        />
        <LeftNavItem
          title="Compliance Overview"
          path="/reports/compliance-overview"
          persistQueries={["accountId"]}
        />
        <LeftNavItem
          title="Utility Overview"
          path="/reports/utility-overview"
          persistQueries={["accountId"]}
        />
      </LeftNavItem>
      {isDemoEnabled && (
        <>
          <ExternalLinkItem link={dataPackageLink}>
            <LeftNavItem title="Data Package" iconName="view-drawing" />
          </ExternalLinkItem>
          <ExternalLinkItem link={insuranceSOVLink}>
            <LeftNavItem title="Insurance - SOV" iconName="view-drawing" />
          </ExternalLinkItem>
        </>
      )}
    </>
  );

  return (
    <LeftNav>
      <RebatesItem />
      <PortfolioNavGroup>{portfolioItems}</PortfolioNavGroup>
      <SelectedBuildingSection />
    </LeftNav>
  );
}
